import type { ReactNode } from "react";
import { Tag } from "./Tag";
import { StatusDot } from "./StatusDot";

export type MeetingState =
  | "in_meeting"
  | "meeting_over"
  | "finalizing"
  | "finalized";

const STATE_PILL: Record<
  MeetingState,
  { label: string; color: string; pulse: boolean }
> = {
  in_meeting: { label: "In meeting", color: "var(--live)", pulse: true },
  meeting_over: { label: "Meeting over", color: "var(--warn)", pulse: false },
  finalizing: { label: "Finalizing", color: "var(--warn)", pulse: true },
  finalized: { label: "Finalized", color: "var(--ok)", pulse: false },
};

/** Top bar for `/meetings/<id>` across all lifecycle states.
 *  Matches design-handoff/project/screens-meeting.jsx:MeetingTopBar.
 */
export function MeetingTopBar({
  meetingId,
  state,
  elapsed,
  right,
}: {
  meetingId: string;
  state: MeetingState;
  /** "MM:SS" or "HH:MM:SS" string; omitted for non-elapsing states. */
  elapsed?: string;
  /** Action slot — Tags + buttons rendered right-aligned. */
  right?: ReactNode;
}) {
  const pill = STATE_PILL[state];
  return (
    <div
      className="recspec-topbar"
      style={{
        padding: "14px 24px",
        borderBottom: "1px solid var(--rule)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        background: "var(--card)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="mono dim" style={{ fontSize: 11 }}>
          /meetings/
        </span>
        <span className="mono" style={{ fontSize: 13, fontWeight: 500 }}>
          {meetingId}
        </span>
      </div>
      <span style={{ width: 1, height: 20, background: "var(--rule)" }} />
      <Tag dot>
        <StatusDot color={pill.color} pulse={pill.pulse} size={6} />
        {pill.label}
      </Tag>
      {elapsed && (
        <span className="mono dim" style={{ fontSize: 11.5 }}>
          · {elapsed} elapsed
        </span>
      )}
      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {right}
      </div>
    </div>
  );
}
